/**
 * Administration data access: TanStack Query hooks over the typed client. Every write is an
 * action on the server (`perform`) and returns its result (message, data, links).
 */
import { keepPreviousData, type QueryKey, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/lib/api/client';
import type {
  ActionOut,
  AiPolicyPatch,
  ApiKeyCreate,
  CompanyCreate,
  CompanyPatch,
  GrantByEmail,
  InviteUser,
  NewsCreate,
  NewsPatch,
  ProjectCreate,
  ProjectPatch,
  RoleCreate,
  RolePatch,
  SetModelSettings,
  SettingLevel,
  SystemHealth,
  UserPatch,
} from './types';

export const adminKeys = {
  all: ['admin'] as const,
  companies: (p: object) => ['admin', 'companies', p] as const,
  company: (id: string) => ['admin', 'company', id] as const,
  projects: (p: object) => ['admin', 'projects', p] as const,
  project: (id: string) => ['admin', 'project', id] as const,
  users: (p: object) => ['admin', 'users', p] as const,
  user: (id: string) => ['admin', 'user', id] as const,
  userRoles: (id: string) => ['admin', 'user-roles', id] as const,
  effective: (id: string) => ['admin', 'effective', id] as const,
  assignments: (p: object) => ['admin', 'assignments', p] as const,
  roles: ['admin', 'roles'] as const,
  role: (id: string) => ['admin', 'role', id] as const,
  personas: ['admin', 'personas'] as const,
  permissions: ['admin', 'permissions'] as const,
  modules: ['admin', 'modules'] as const,
  apiKeys: (p: object) => ['admin', 'api-keys', p] as const,
  registry: ['admin', 'settings', 'registry'] as const,
  projectSettings: (id: string) => ['admin', 'settings', 'project', id] as const,
  vehicleSettings: (id: string) => ['admin', 'settings', 'vehicle', id] as const,
  matrix: (p: object) => ['admin', 'settings', 'matrix', p] as const,
  news: (p: object) => ['admin', 'news', p] as const,
  accessLog: (p: object) => ['admin', 'access-log', p] as const,
  aiUsage: (days: number) => ['admin', 'ai-usage', days] as const,
  aiOptions: ['admin', 'ai-options'] as const,
  health: ['admin', 'health'] as const,
  aiModels: ['ai', 'models'] as const,
};

const PAGE = 100;
const STATIC = 10 * 60_000;
const next = (last: { next_cursor?: string | null }) => last.next_cursor ?? undefined;

// ---------------------------------------------------------------- companies

export function useCompany(id: string | undefined) {
  return useQuery({
    queryKey: adminKeys.company(id ?? ''),
    queryFn: () => unwrap(api.GET('/api/admin/companies/{company_id}', { params: { path: { company_id: id ?? '' } } })),
    enabled: !!id,
  });
}

export interface CompanyQuery {
  q?: string;
  active?: boolean;
}

export function useCompanies(p: CompanyQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: adminKeys.companies(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/admin/companies', { params: { query: { ...p, cursor: pageParam, limit: PAGE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: next,
    placeholderData: keepPreviousData,
    enabled,
  });
}

// ---------------------------------------------------------------- projects

export interface ProjectQuery {
  q?: string;
  active?: boolean;
}

export function useProjects(p: ProjectQuery = {}, enabled = true) {
  return useInfiniteQuery({
    queryKey: adminKeys.projects(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/admin/projects', { params: { query: { ...p, cursor: pageParam, limit: PAGE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: next,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: adminKeys.project(id),
    queryFn: () => unwrap(api.GET('/api/admin/projects/{project_id}', { params: { path: { project_id: id } } })),
  });
}

// ---------------------------------------------------------------- users and grants

export interface UserQuery {
  q?: string;
  active?: boolean;
  role_id?: string;
  external?: boolean;
  invite?: 'pending' | 'never_signed_in' | 'accepted';
}

export function useUsers(p: UserQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: adminKeys.users(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/admin/users', { params: { query: { ...p, cursor: pageParam, limit: PAGE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: next,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: adminKeys.user(id),
    queryFn: () => unwrap(api.GET('/api/admin/users/{user_id}', { params: { path: { user_id: id } } })),
  });
}

export function useUserRoles(id: string) {
  return useQuery({
    queryKey: adminKeys.userRoles(id),
    queryFn: () => unwrap(api.GET('/api/admin/users/{user_id}/roles', { params: { path: { user_id: id } } })),
  });
}

/** "View as": read-only, and the server writes every use to the access log. */
export function useEffectivePermissions(id: string, enabled = true) {
  return useQuery({
    queryKey: adminKeys.effective(id),
    queryFn: () => unwrap(api.GET('/api/admin/users/{user_id}/effective-permissions', { params: { path: { user_id: id } } })),
    enabled,
    staleTime: 60_000,
  });
}

export interface AssignmentQuery {
  role_id?: string;
  project_id?: string;
  cross_company?: boolean;
}

export function useAssignments(p: AssignmentQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: adminKeys.assignments(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/admin/role-assignments', { params: { query: { ...p, cursor: pageParam, limit: PAGE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: next,
    placeholderData: keepPreviousData,
    enabled,
  });
}

// ---------------------------------------------------------------- roles, personas, catalogues

export function useRoles(enabled = true) {
  return useQuery({ queryKey: adminKeys.roles, queryFn: () => unwrap(api.GET('/api/admin/roles')), enabled });
}

export function useRole(id: string) {
  return useQuery({
    queryKey: adminKeys.role(id),
    queryFn: () => unwrap(api.GET('/api/admin/roles/{role_id}', { params: { path: { role_id: id } } })),
  });
}

export function usePersonas(enabled = true) {
  return useQuery({ queryKey: adminKeys.personas, queryFn: () => unwrap(api.GET('/api/admin/personas')), staleTime: STATIC, enabled });
}

export function usePermissionCatalogue(enabled = true) {
  return useQuery({ queryKey: adminKeys.permissions, queryFn: () => unwrap(api.GET('/api/admin/permissions')), staleTime: STATIC, enabled });
}

export function useModuleCatalogue(enabled = true) {
  return useQuery({ queryKey: adminKeys.modules, queryFn: () => unwrap(api.GET('/api/admin/modules')), staleTime: STATIC, enabled });
}

// ---------------------------------------------------------------- API keys

export function useApiKeys(p: { include_revoked?: boolean }, enabled = true) {
  return useInfiniteQuery({
    queryKey: adminKeys.apiKeys(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/admin/api-keys', { params: { query: { ...p, cursor: pageParam, limit: PAGE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: next,
    placeholderData: keepPreviousData,
    enabled,
  });
}

// ---------------------------------------------------------------- settings

export function useSettingsRegistry(enabled = true) {
  return useQuery({ queryKey: adminKeys.registry, queryFn: () => unwrap(api.GET('/api/admin/settings/registry')), staleTime: STATIC, enabled });
}

export function useProjectSettings(projectId: string | undefined) {
  return useQuery({
    queryKey: adminKeys.projectSettings(projectId ?? ''),
    queryFn: () => unwrap(api.GET('/api/admin/settings/projects/{project_id}', { params: { path: { project_id: projectId ?? '' } } })),
    enabled: !!projectId,
    placeholderData: keepPreviousData,
  });
}

export function useVehicleSettings(vehicleId: string) {
  return useQuery({
    queryKey: adminKeys.vehicleSettings(vehicleId),
    queryFn: () => unwrap(api.GET('/api/admin/settings/vehicles/{vehicle_id}', { params: { path: { vehicle_id: vehicleId } } })),
  });
}

export function useSettingMatrix(p: { key: string; project_id: string | undefined; status?: string }) {
  return useInfiniteQuery({
    queryKey: adminKeys.matrix(p),
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET('/api/admin/settings/matrix', {
          params: { query: { key: p.key, project_id: p.project_id ?? '', status: p.status, cursor: pageParam, limit: 200 } },
        }),
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: next,
    placeholderData: keepPreviousData,
    enabled: !!p.project_id,
  });
}

// ---------------------------------------------------------------- news, access log, AI, health

export function useNewsPosts(p: { include_archived?: boolean }, enabled = true) {
  return useInfiniteQuery({
    queryKey: adminKeys.news(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/admin/news', { params: { query: { ...p, cursor: pageParam, limit: 50 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: next,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export interface AccessLogQuery {
  actor_id?: string;
  resource?: string;
  resource_id?: string;
  since?: string;
  until?: string;
}

export function useAccessLog(p: AccessLogQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: adminKeys.accessLog(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/admin/access-log', { params: { query: { ...p, cursor: pageParam, limit: PAGE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: next,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useAiUsage(days: number, enabled = true) {
  return useQuery({
    queryKey: adminKeys.aiUsage(days),
    queryFn: () => unwrap(api.GET('/api/admin/ai/usage', { params: { query: { days } } })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useAiPolicyOptions(enabled = true) {
  return useQuery({ queryKey: adminKeys.aiOptions, queryFn: () => unwrap(api.GET('/api/admin/ai/policy-options')), staleTime: STATIC, enabled });
}

/** Settings → AI: provider (deployment), family, pinned model, tiers per capability, candidates. */
export function useAiModels(enabled = true) {
  return useQuery({ queryKey: adminKeys.aiModels, queryFn: () => unwrap(api.GET('/api/ai/models')), enabled, staleTime: 5 * 60_000 });
}

/** Re-read the gateway's model list; the answer replaces the cached settings. */
export function useRefreshAiModels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/ai/models/refresh')),
    onSuccess: (data) => qc.setQueryData(adminKeys.aiModels, data),
  });
}

/** Family, pin and tiers (action ai.set_model_settings, high risk: a person always confirms). */
export const useSetAiModels = () => useWrite((body: SetModelSettings) => unwrap(api.PATCH('/api/ai/models', { body })), [adminKeys.aiModels, ME]);

export function useSystemHealth(enabled = true) {
  return useQuery({
    queryKey: adminKeys.health,
    // GAP: /api/system/health is untyped in core; see SystemHealth in ./types.ts.
    queryFn: async () => (await unwrap(api.GET('/api/system/health'))) as unknown as SystemHealth,
    refetchInterval: 60_000,
    enabled,
  });
}

// ---------------------------------------------------------------- writes

/** A write whose success makes the admin queries (and optionally more) stale. */
function useWrite<V>(fn: (v: V) => Promise<ActionOut>, alsoStale: QueryKey[] = []) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.all });
      for (const key of alsoStale) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

const ME: QueryKey = ['me'];

/**
 * The record a form edits: its id and the version the form was loaded with. Every edit sends that
 * version, so an edit made on a stale screen is refused (409) instead of silently overwriting
 * someone else's change (RULES §WRITE-3); `ActionError` shows the server's "reload" message.
 */
export interface Edited {
  id: string;
  version: number;
}

export const useCreateCompany = () => useWrite((body: CompanyCreate) => unwrap(api.POST('/api/admin/companies', { body })));

export const useUpdateCompany = (c: Edited) =>
  useWrite(
    (body: CompanyPatch) =>
      unwrap(api.PATCH('/api/admin/companies/{company_id}', { params: { path: { company_id: c.id } }, body: { ...body, version: c.version } })),
    [ME],
  );

export const useSetCompanyModules = (id: string) =>
  useWrite(
    (modules: string[]) => unwrap(api.PUT('/api/admin/companies/{company_id}/modules', { params: { path: { company_id: id } }, body: { modules } })),
    [ME],
  );

export const useSetAiPolicy = (c: Edited) =>
  useWrite(
    (body: AiPolicyPatch) =>
      unwrap(api.PATCH('/api/admin/companies/{company_id}/ai', { params: { path: { company_id: c.id } }, body: { ...body, version: c.version } })),
    [ME],
  );

export const useCreateProject = () => useWrite((body: ProjectCreate) => unwrap(api.POST('/api/admin/projects', { body })), [ME]);

export const useUpdateProject = (p: Edited) =>
  useWrite(
    (body: ProjectPatch) =>
      unwrap(api.PATCH('/api/admin/projects/{project_id}', { params: { path: { project_id: p.id } }, body: { ...body, version: p.version } })),
    [ME],
  );

export const useSetProjectModules = (id: string) =>
  useWrite(
    (modules: string[]) => unwrap(api.PUT('/api/admin/projects/{project_id}/modules', { params: { path: { project_id: id } }, body: { modules } })),
    [ME],
  );

export const useInviteUser = () => useWrite((body: InviteUser) => unwrap(api.POST('/api/admin/users', { body })));

export const useUpdateUser = (u: Edited) =>
  useWrite((body: UserPatch) =>
    unwrap(api.PATCH('/api/admin/users/{user_id}', { params: { path: { user_id: u.id } }, body: { ...body, version: u.version } })),
  );

export const useSetUserActive = (id: string) =>
  useWrite(({ active, reason }: { active: boolean; reason: string }) =>
    active
      ? unwrap(api.POST('/api/admin/users/{user_id}/reactivate', { params: { path: { user_id: id }, query: { reason } } }))
      : unwrap(api.POST('/api/admin/users/{user_id}/deactivate', { params: { path: { user_id: id }, query: { reason } } })),
  );

export const useSendPasswordReset = (id: string) =>
  useWrite(() => unwrap(api.POST('/api/admin/users/{user_id}/password-reset', { params: { path: { user_id: id } } })));

export const useGrantRole = () => useWrite((body: GrantByEmail) => unwrap(api.POST('/api/admin/role-assignments', { body })));

export const useRevokeGrant = () =>
  useWrite(({ id, reason }: { id: string; reason: string }) =>
    unwrap(api.DELETE('/api/admin/role-assignments/{assignment_id}', { params: { path: { assignment_id: id }, query: { reason } } })),
  );

export const useCreateRole = () => useWrite((body: RoleCreate) => unwrap(api.POST('/api/admin/roles', { body })));

export const useUpdateRole = (r: Edited) =>
  useWrite(
    (body: RolePatch) => unwrap(api.PATCH('/api/admin/roles/{role_id}', { params: { path: { role_id: r.id } }, body: { ...body, version: r.version } })),
    [ME],
  );

export const useDeleteRole = (id: string) => useWrite(() => unwrap(api.DELETE('/api/admin/roles/{role_id}', { params: { path: { role_id: id } } })));

export const useCreateApiKey = () => useWrite((body: ApiKeyCreate) => unwrap(api.POST('/api/admin/api-keys', { body })));

export const useRotateApiKey = () =>
  useWrite((id: string) => unwrap(api.POST('/api/admin/api-keys/{api_key_id}/rotate', { params: { path: { api_key_id: id } } })));

export const useRevokeApiKey = () =>
  useWrite((id: string) => unwrap(api.POST('/api/admin/api-keys/{api_key_id}/revoke', { params: { path: { api_key_id: id } } })));

export const useTransferApiKey = () =>
  useWrite(({ id, to }: { id: string; to: string }) =>
    unwrap(api.POST('/api/admin/api-keys/{api_key_id}/transfer', { params: { path: { api_key_id: id } }, body: { to_user_id: to } })),
  );

export interface SettingTarget {
  key: string;
  level: SettingLevel;
  level_id: string;
}

/** `version`: of the value stored at that level when the editor opened; 0 when it inherited (RULES §WRITE-3). */
export const useSetSetting = () =>
  useWrite(({ value, version, ...path }: SettingTarget & { value: unknown; version: number | undefined }) =>
    unwrap(api.PUT('/api/admin/settings/{key}/{level}/{level_id}', { params: { path }, body: { value, version } })),
  );

/** Reset to inherit: remove the value stored at this level (the version it had when the editor opened). */
export const useClearSetting = () =>
  useWrite(({ version, ...path }: SettingTarget & { version: number | undefined }) =>
    unwrap(api.DELETE('/api/admin/settings/{key}/{level}/{level_id}', { params: { path, query: { version } } })),
  );

export const useCreateNews = () => useWrite((body: NewsCreate) => unwrap(api.POST('/api/admin/news', { body })));

export const useUpdateNews = (id: string) =>
  useWrite((body: NewsPatch) => unwrap(api.PATCH('/api/admin/news/{news_post_id}', { params: { path: { news_post_id: id } }, body })));

export const useArchiveNews = () =>
  useWrite((id: string) => unwrap(api.POST('/api/admin/news/{news_post_id}/archive', { params: { path: { news_post_id: id } } })));
